import { useState } from 'react';
import { Button } from './ui/button';
import { 
  Share2, 
  Twitter, 
  Facebook, 
  Linkedin, 
  Copy, 
  CheckCircle2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

export default function ShareScoreModal({ score, grade, onClose }) {
  const [copied, setCopied] = useState(false);
  
  const shareText = `🛡️ My Privacy Score: ${score}/100 (Grade ${grade})!\n\nI'm taking control of my digital privacy with PrivacyShield. Find out your score and protect yourself from surveillance tools like Fog Reveal.`;
  const shareUrl = 'https://privacyshield.store';

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getGradeEmoji = (grade) => {
    switch (grade) {
      case 'A': return '🏆';
      case 'B': return '✅';
      case 'C': return '⚠️';
      case 'D': return '🔶';
      default: return '🚨';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="share-score-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0a0a0a] border border-[#262626] rounded-2xl p-6 max-w-sm w-full animate-slide-up">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Score Display */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-2">{getGradeEmoji(grade)}</div>
          <h3 className="font-heading font-bold text-xl text-white mb-1">
            Share Your Score
          </h3>
          <p className="text-gray-500 text-sm">
            Let your friends know about digital privacy
          </p>
        </div>

        {/* Preview Card */}
        <div className="bg-[#171717] border border-[#262626] rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center">
              <span className="font-mono font-bold text-[#00FF94]">{score}</span>
            </div>
            <div>
              <p className="text-white font-medium">Privacy Score</p>
              <p className="text-xs text-gray-500">Grade {grade}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 line-clamp-2">
            {shareText.slice(0, 100)}...
          </p>
        </div>

        {/* Share Buttons */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <a 
            href={shareLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-3 bg-[#171717] border border-[#262626] rounded-lg hover:border-[#1DA1F2]/50 transition-colors group"
            data-testid="share-twitter"
          >
            <Twitter className="w-5 h-5 text-gray-400 group-hover:text-[#1DA1F2]" />
            <span className="text-xs text-gray-500">Twitter</span>
          </a>
          <a 
            href={shareLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-3 bg-[#171717] border border-[#262626] rounded-lg hover:border-[#4267B2]/50 transition-colors group"
            data-testid="share-facebook"
          >
            <Facebook className="w-5 h-5 text-gray-400 group-hover:text-[#4267B2]" />
            <span className="text-xs text-gray-500">Facebook</span>
          </a>
          <a 
            href={shareLinks.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-3 bg-[#171717] border border-[#262626] rounded-lg hover:border-[#0A66C2]/50 transition-colors group"
            data-testid="share-linkedin"
          >
            <Linkedin className="w-5 h-5 text-gray-400 group-hover:text-[#0A66C2]" />
            <span className="text-xs text-gray-500">LinkedIn</span>
          </a>
          <button 
            onClick={copyToClipboard}
            className="flex flex-col items-center gap-1 p-3 bg-[#171717] border border-[#262626] rounded-lg hover:border-[#00FF94]/50 transition-colors group"
            data-testid="share-copy"
          >
            {copied ? (
              <CheckCircle2 className="w-5 h-5 text-[#00FF94]" />
            ) : (
              <Copy className="w-5 h-5 text-gray-400 group-hover:text-[#00FF94]" />
            )}
            <span className="text-xs text-gray-500">Copy</span>
          </button>
        </div>

        <p className="text-xs text-gray-600 text-center">
          Help spread privacy awareness!
        </p>
      </div>
    </div>
  );
}
